import {SlideshowEvents} from './slideshow'
import {inject, injectable} from 'inversify'
import {SlideData} from "./Core/SlideshowData";
import {PubSub} from "./Core/PubSub";
import {settings, SettingsEvents} from "./Core/Settings";


@injectable()
export class Synchroniser {
    private listeners: TimeListener[] = [];
    private playState: PlayState = PlayState.Playing;
    private slideStartTime: number;
    private pausedProgress: number;
    private autoProceed: boolean = true;
    private playbackSpeedInternal = 1;

    constructor(@inject("PubSub") private pubSub: PubSub) {
        pubSub.subscribeToEvent(SlideshowEvents.SlideEntered, slideData => this.slideEntered(slideData));
        pubSub.subscribeToEvent(SettingsEvents.playbackSpeed, s => this.playbackSpeed = s);
    }

    slideEntered(slide: SlideData){
        this.autoProceed = slide.autoProceed;

        this.slideStartTime = performance.now();
        this.pausedProgress = 0;
        for (let cp of this.contentPlayers) {
            cp.finished = false;
        }
    }

    /* Gets the progress of a slide in seconds */
    getSlideProgress(): number {
        if (this.playState === PlayState.Playing && this.slideStartTime) {
            let raw = (performance.now() - this.slideStartTime) / 1000;
            return raw * this.playbackSpeed;
        }
        else if (this.playState === PlayState.Paused) {
            return this.pausedProgress * this.playbackSpeed;
        }
        
        return 0;
    }

    /* Gets the duration of a slide in seconds */
    getSlideDuration(): number {
        let duration = 0;

        for (let l of this.listeners) {
            if (!l.endTime) continue;
            
            let et = l.endTime();
            duration = et > length ? et : duration;
        }

        return duration;
    }

    stop(): void {
        if (this.playState === PlayState.Stopped) return;

        this.playState = PlayState.Stopped;
        
        for (let i = 0; i < this.listeners.length; i++) {
            let l = this.listeners[i];
            l.stopping?.();
        }

        for (let cp of this.contentPlayers) {
            cp.finished = false;
        }
    }

    play(): Promise<void> {
        if (this.playState === PlayState.Playing) return Promise.resolve();

        if (this.playState === PlayState.Paused) {
            this.slideStartTime = performance.now() - (this.pausedProgress * 1000);
        }
        else {
            this.slideStartTime = performance.now();
        }

        this.playState = PlayState.Playing;
        let promises: Promise<void>[] = [];
        
        for (let i = 0; i < this.listeners.length; i++) {
            let l = this.listeners[i];
            let result = l.playing?.();
            if (result)
                promises.push(result);
        }

        return Promise.all(promises).then();
    }

    pause(): void {
        if (this.playState !== PlayState.Playing) return;

        this.pausedProgress = this.getSlideProgress();
        this.playState = PlayState.Paused;
        
        for (let i = 0; i < this.listeners.length; i++) {
            let l = this.listeners[i];
            l.pausing?.();
        }
    }

    seek(time: number) {
        if (this.playState === PlayState.Stopped) {
            this.playState = PlayState.Paused;
        }
        
        this.slideStartTime = performance.now() - time * 1000;
        this.pausedProgress = time;

        for (let cp of this.contentPlayers) {
            cp.finished = false;
        }

        for (let i = 0; i < this.listeners.length; i++) {
            let l = this.listeners[i];
            l.seek?.(time);
        }
    }

    get playbackSpeed() {
        return this.playbackSpeedInternal;
    }

    set playbackSpeed(speed: number) {
        let currentProgress = this.getSlideProgress();
        this.slideStartTime = performance.now() - (currentProgress * 1000) / speed;
        this.playbackSpeedInternal = speed;

        for (let i = 0; i < this.listeners.length; i++) {
            let l = this.listeners[i];
            l.setPlaybackSpeed?.(speed);
        }
    }

    getPlayState(): PlayState {
        return this.playState;
    }

    get isPlaying(): boolean {
        return this.playState == PlayState.Playing;
    }

    registerTimeListener(l: TimeListener) {
        this.listeners.push(l);
    }

    deregisterTimeListener(l: TimeListener) {
        let i = this.listeners.indexOf(l);
        if (i > -1)
            this.listeners.splice(i , 1);
    }

    private contentPlayers: ContentPlayerInfo[] = [];

    registerContentPlayer(cp: ContentPlayer): ContentPlayerHandler {
        let index = this.contentPlayers.length;
        this.contentPlayers.push({player: cp, timeOffset: 0, finished: false});
        return new ContentPlayerHandler(this, index);
    }

    contentPlayerFinished(index: number): Promise<void> {
        this.contentPlayers[index].finished = true;

        if (this.contentPlayers.every(p => p.finished) && this.autoProceed && !settings.readerMode)
            return this.pubSub.publishEvent(SyncEvents.ContentFinished, {});

        return Promise.resolve();
    }
}

interface ContentPlayerInfo {
    player: ContentPlayer;
    timeOffset: number;
    finished: boolean;
}

export class ContentPlayerHandler {
    constructor(private sync: Synchroniser, private handlerIndex: number) {
    }

    finished(): Promise<void> {
        return this.sync.contentPlayerFinished(this.handlerIndex);
    }
}

export interface ContentPlayer {
}


export enum PlayState {
    Playing,
    Paused,
    Stopped,
    Finished
}

export interface TimeListener {
    playing?(): Promise<void>;
    stopping?(): void;
    pausing?(): void;
    seek?(time: number): void;
    endTime?(): number;
    setPlaybackSpeed?(speed: number): void;
}

export enum SyncEvents {
    ContentFinished = "ContentFinished"
}